import type { TFunction } from 'i18next'
import { splitPresentationText } from './chatPresentationSegments'
import { leavesForeignProseUntranslated } from './presentationMixGuard'

export type PresentationField =
  | { kind: 'identity'; value: string }
  | { kind: 'catalog'; value: string; key: string; fallbackKey?: string }
  | {
      kind: 'text'
      value: string
      sourceLocale?: string
      localized?: ReadonlyArray<{ locale: string; text: string }>
      /** Every proper name embedded in free text must be supplied here. */
      protectedValues?: readonly string[]
      /** Codes/units are protected only as complete Unicode-delimited tokens. */
      protectedTokenValues?: readonly string[]
    }

export interface PresentedText {
  text: string
  state: 'original' | 'catalog' | 'localized' | 'translated' | 'pending' | 'unavailable'
}

/** Integrator supplies an authenticated engine adapter, never a provider credential. */
export type PresentationTranslator = (request: {
  text: string
  sourceLocale?: string
  targetLocale: string
  signal: AbortSignal
}) => Promise<string>

const CODE = /\b[A-Za-z0-9]+(?:[-/.][A-Za-z0-9]+)*\b/g
const TOKEN = /__WO_KEEP_\d+__/g
const language = (locale: string) => locale.toLowerCase().split('-')[0]

function protect(field: Extract<PresentationField, { kind: 'text' }>) {
  const codes = (field.value.match(CODE) ?? []).filter((value) => /\d/.test(value))
  const tokenValues = new Set(field.protectedTokenValues ?? [])
  const values = [...new Set([...(field.protectedValues ?? []), ...codes, ...tokenValues])]
    // A shared scope may carry hundreds of unrelated names/codes. Exclude
    // absent literals before compiling their Unicode token-boundary patterns.
    .filter((value) => Boolean(value) && field.value.includes(value))
    .sort((a, b) => b.length - a.length)
  const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const pattern = values
    .map((value) =>
      tokenValues.has(value)
        ? `(?<![\\p{L}\\p{N}])${escape(value)}(?![\\p{L}\\p{N}])`
        : escape(value),
    )
    .join('|')
  const originals: string[] = []
  const text = pattern
    ? field.value.replace(new RegExp(pattern, 'gu'), (value) => {
        const token = `__WO_KEEP_${originals.length}__`
        originals.push(value)
        return token
      })
    : field.value
  return { text, originals }
}

function restore(text: string, originals: string[]): string | undefined {
  if (!text.trim()) return undefined
  const tokens = text.match(TOKEN) ?? []
  if (
    tokens.length !== originals.length ||
    originals.some(
      (_, index) => tokens.filter((token) => token === `__WO_KEEP_${index}__`).length !== 1,
    )
  )
    return undefined
  return text.replace(TOKEN, (token) => originals[Number(token.match(/\d+/)?.[0])])
}

/** Admission uses the exact protected payload, not the unprotected display size. */
export function presentationFieldCost(field: Extract<PresentationField, { kind: 'text' }>) {
  const text = protect(field).text
  const pieces = splitPresentationText(text).filter(piece => piece.text)
  return { text, entries: pieces.length, characters: pieces.reduce((sum, piece) => sum + piece.text.length, 0) }
}

/** Create once per authenticated tenant/user context; clear on logout/context change. */
export function createWorkOrderPresenter(
  options: {
    translate?: PresentationTranslator
    timeoutMs?: number
    cacheSize?: number
    /** R210: motivo (sem texto) de cada tradução que não chegou à tela. */
    diagnose?: (reason: string, value: string) => void
    /**
     * R219: a tradução que chegou mas NÃO passou nas travas daqui (marcador perdido, mistura de
     * idiomas) tem de sair da memória de quem a entregou; senão o "tentar de novo" recebe a mesma
     * resposta ruim sem pedir nada. Recebe o texto protegido enviado e o idioma.
     */
    discard?: (sent: string, locale: string) => void
  } = {},
) {
  const cache = new Map<string, string>()
  const pending = new Map<string, Promise<string | undefined>>()
  const controllers = new Set<AbortController>()
  let generation = 0

  const present = (field: PresentationField, locale: string, t: TFunction) =>
    presentField(field, locale, t, cache, Boolean(options.translate))

  async function request(field: Extract<PresentationField, { kind: 'text' }>, locale: string) {
    if (!options.translate) return undefined
    const controller = new AbortController()
    controllers.add(controller)
    const capturedGeneration = generation
    const restored = await translateField(
      field,
      locale,
      options.translate,
      controller,
      options.timeoutMs ?? 3000,
      options.diagnose,
      options.discard,
    )
    controllers.delete(controller)
    if (capturedGeneration !== generation) {
      options.diagnose?.('generation_changed', field.value)
      return undefined
    }
    if (restored !== undefined) storeInCache(cache, keyFor(field, locale), restored, options.cacheSize ?? 200)
    return restored
  }

  async function resolve(
    field: PresentationField,
    locale: string,
    t: TFunction,
  ): Promise<PresentedText> {
    const initial = present(field, locale, t)
    if (initial.state !== 'pending' || field.kind !== 'text') return initial
    const key = keyFor(field, locale)
    let promise = pending.get(key)
    if (!promise) {
      promise = request(field, locale)
      pending.set(key, promise)
      void promise.finally(() => {
        if (pending.get(key) === promise) pending.delete(key)
      })
    }
    const translated = await promise
    return translated === undefined
      ? { text: field.value, state: 'unavailable' }
      : { text: translated, state: 'translated' }
  }

  return {
    present,
    resolve,
    clear() {
      generation++
      cache.clear()
      pending.clear()
      controllers.forEach((controller) => controller.abort())
    },
  }
}

function storeInCache(cache: Map<string, string>, key: string, value: string, size: number) {
  cache.set(key, value)
  while (cache.size > Math.max(1, size)) cache.delete(cache.keys().next().value!)
}

function keyFor(field: Extract<PresentationField, { kind: 'text' }>, locale: string) {
  return JSON.stringify([
    locale,
    field.sourceLocale,
    field.value,
    field.protectedValues ?? [],
    field.protectedTokenValues ?? [],
  ])
}

export type WorkOrderPresenter = ReturnType<typeof createWorkOrderPresenter>

function presentField(
  field: PresentationField,
  locale: string,
  t: TFunction,
  cache: Map<string, string>,
  canTranslate: boolean,
): PresentedText {
  if (field.kind === 'identity') return { text: field.value, state: 'original' }
  if (field.kind === 'catalog') {
    const missing = '__WO_MISSING__'
    const text = t(field.key, { defaultValue: missing })
    return {
      text: text === missing ? t(field.fallbackKey ?? 'workOrderDetail.status.desconhecido') : text,
      state: 'catalog',
    }
  }
  const supplied = suppliedText(field, locale)
  if (supplied?.text) return { text: supplied.text, state: 'localized' }
  if (field.value === 'Inspecionar e diagnosticar falha relatada no equipamento') {
    return { text: t('operationsPanel.defaultInspection'), state: 'catalog' }
  }
  if (!field.value.trim() || (field.sourceLocale && language(field.sourceLocale) === language(locale))) {
    return { text: field.value, state: 'original' }
  }
  const cached = cache.get(keyFor(field, locale))
  if (cached !== undefined) return { text: cached, state: 'translated' }
  return { text: field.value, state: canTranslate ? 'pending' : 'unavailable' }
}

/** Restore the protected markers and apply the foreign-prose guard to a translation that arrived. */
async function checkTranslation(
  field: Extract<PresentationField, { kind: 'text' }>,
  locale: string,
  translated: string,
  sent: { text: string; originals: ReturnType<typeof protect>['originals'] },
  diagnose?: (reason: string, value: string) => void,
  discard?: (sent: string, locale: string) => void,
): Promise<string | undefined> {
  const restored = restore(translated, sent.originals)
  if (restored === undefined) {
    diagnose?.('token_mismatch', field.value)
    discard?.(sent.text, locale)
  }
  // Metade traduzida, metade no idioma de origem nunca chega na tela.
  if (
    restored !== undefined &&
    (await leavesForeignProseUntranslated(field.value, restored, locale, [
      ...(field.protectedValues ?? []),
      ...(field.protectedTokenValues ?? []),
    ]))
  ) {
    diagnose?.('mix_guard', field.value)
    discard?.(sent.text, locale)
    return undefined
  }
  return restored
}

async function translateField(
  field: Extract<PresentationField, { kind: 'text' }>,
  locale: string,
  translate: PresentationTranslator,
  controller: AbortController,
  timeoutMs: number,
  diagnose?: (reason: string, value: string) => void,
  discard?: (sent: string, locale: string) => void,
): Promise<string | undefined> {
  if (field.value.match(TOKEN)) {
    diagnose?.('source_has_token', field.value)
    return undefined
  }
  const { text, originals } = protect(field)
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const aborted = new Promise<undefined>((resolve) => {
      controller.signal.addEventListener('abort', () => resolve(undefined), { once: true })
      timer = setTimeout(() => controller.abort(), timeoutMs)
    })
    const translated = await Promise.race([
      Promise.resolve().then(() =>
        translate({
          text,
          sourceLocale: field.sourceLocale,
          targetLocale: locale,
          signal: controller.signal,
        }),
      ),
      aborted,
    ])
    if (typeof translated !== 'string' || controller.signal.aborted) {
      diagnose?.(controller.signal.aborted ? 'aborted_or_timeout' : 'not_string', field.value)
      return undefined
    }
    return await checkTranslation(field, locale, translated, { text, originals }, diagnose, discard)
  } catch (error) {
    diagnose?.(`translate_error:${error instanceof Error ? error.message.slice(0, 24) : 'unknown'}`, field.value)
    return undefined
  } finally {
    clearTimeout(timer)
  }
}

function suppliedText(field: Extract<PresentationField, { kind: 'text' }>, locale: string) {
  return (
    field.localized?.find((entry) => entry.locale.toLowerCase() === locale.toLowerCase()) ??
    field.localized?.find((entry) => entry.locale.toLowerCase() === language(locale))
  )
}
